import { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { downloadClientOEMStatementPDF, getClientInvoiceDoc } from '../../../api/api'
import { FileUpload } from '@components/file-upload'
import { FILE_UPLOAD } from '../../../constants'
import { Logger } from '../../../logger'
import { AdasTypography, AdasContainer, AdasLink, AdasButton, AdasBox, AdasCircularProgress } from '@components/wrapper-components'

const REPORT_KEYS = ['preCalibrationReport', 'oemProcedure', 'wheelAlignmentReport', 'calibrationReport', 'postTestScanReport']
const ADDITIONAL_NAMED_KEYS = ['collisionRepairEstimate', 'calibrationRequiredReport', 'partsInvoice']

const PANEL = 'mb-4 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'
const PANEL_HEADING = 'text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'

export const ClientInvoiceDocumentPage = () => {
  const history = useHistory()
  const location = useLocation()
  const workOrderData = location.state
  const [documents, setDocuments] = useState({
    preCalibrationReport: '',
    oemProcedure: '',
    wheelAlignmentReport: '',
    calibrationReport: '',
    postTestScanReport: '',
    collisionRepairEstimate: '',
    calibrationRequiredReport: '',
    partsInvoice: '',
    qwiOemPositionStatements: []
  })
  const [additionalDocuments, setAdditionalDocuments] = useState([])
  const [loadedAdditionalDocs, setLoadedAdditionalDocs] = useState([])
  const [statementLoading, setStatementLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const removeAdditionalFileHandler = (index) => {
    setAdditionalDocuments((prev) => {
      prev[index].removed = true
      const data = [...prev]
      return data
    })
  }

  const removeLoadedAdditionalFileHandler = (index) => {
    setLoadedAdditionalDocs((prev) => {
      prev[index].removed = true
      const data = [...prev]
      return data
    })
  }

  const removeFileHandler = (fileName) => {
    if (fileName) {
      setDocuments((prevState) => {
        const updatedDocuments = {
          ...prevState,
          [fileName]: null
        }
        return updatedDocuments
      })
    }
  }

  useEffect(() => {
    const requestDocuments = async (params) => {
      try {
        const response = await getClientInvoiceDoc(params)
        if (response.status === 200) {
          const documentSummaryData = response.data
          Object.keys(documents).forEach((key) => {
            if (documentSummaryData[key]) {
              setDocuments((prevState) => {
                const updatedDocuments = {
                  ...prevState,
                  [key]: documentSummaryData[key]
                }
                return updatedDocuments
              })
            }
          })
          if (documentSummaryData.additionalDocuments != null) {
            let loadedAdditionalDocs = []
            documentSummaryData.additionalDocuments.forEach((doc) => {
              loadedAdditionalDocs.push(doc)
              if (loadedAdditionalDocs.length > 0) {
                setLoadedAdditionalDocs(() => {
                  const data = [...loadedAdditionalDocs]
                  return data
                })
              }
            })
          }
        }
      } catch (error) {
        setLoadError(true)
        Logger.error({ message: error, payload: { params, path: 'ClientInvoiceDocumentPage/requestDocuments' } })
      }
    }
    const params = {
      token: workOrderData.tempToken,
      invoiceId: workOrderData.id
    }
    requestDocuments(params)
    /* eslint-disable */
  }, [])

  const handleOemStatementClick = useCallback(
    async (event) => {
      const id = event.target.id
      setStatementLoading(id)
      try {
        const dataBlob = await downloadClientOEMStatementPDF({
          token: workOrderData.tempToken,
          invoiceId: workOrderData.id,
          oemPositionStatementsId: id
        })
        const downloadURL = URL.createObjectURL(dataBlob.data)
        window.open(downloadURL, '_blank')
      } catch (error) {
        Logger.error({ message: error, payload: { id, path: 'ClientInvoiceDocumentPage/handleOemStatementClick' } })
      } finally {
        setStatementLoading(false)
      }
    },
    [workOrderData.id, workOrderData.tempToken]
  )

  // Counts shown in each panel header ("X of Y attached"). Y is the full set of
  // document slots; X is how many actually have a file attached for this invoice.
  const { reportsAttached, reportsTotal, additionalAttached, additionalTotal } = useMemo(() => {
    const reportAttached = REPORT_KEYS.reduce((n, k) => n + (documents[k] ? 1 : 0), 0)
    const namedAdditionalAttached = ADDITIONAL_NAMED_KEYS.reduce((n, k) => n + (documents[k] ? 1 : 0), 0)
    const loadedActive = loadedAdditionalDocs.filter((d) => d && !d.removed).length
    const additionalActive = additionalDocuments.filter((d) => d && !d.removed).length
    return {
      reportsAttached: reportAttached,
      reportsTotal: REPORT_KEYS.length,
      additionalAttached: namedAdditionalAttached + loadedActive + additionalActive,
      additionalTotal: ADDITIONAL_NAMED_KEYS.length + loadedAdditionalDocs.length + additionalDocuments.length
    }
  }, [documents, loadedAdditionalDocs, additionalDocuments])

  if (loadError) {
    return (
      <AdasContainer maxWidth='lg'>
        <AdasBox className='mt-5 mb-[60px]'>
          <AdasBox className={`${PANEL} px-[20px] py-[40px] text-center`}>
            <AdasTypography className='mb-[8px] text-[16px] font-semibold text-[#0A1628]'>
              Documents are unavailable for this invoice
            </AdasTypography>
            <AdasTypography className='text-[14px] text-[#6B7280]'>Please contact your calibration center for assistance.</AdasTypography>
          </AdasBox>
          <AdasButton variant='outlined' aria-label='goBack' color='primary' className='rounded-lg px-6 font-semibold' onClick={() => history.goBack()}>
            Return To Summary
          </AdasButton>
        </AdasBox>
      </AdasContainer>
    )
  }

  return (
    <AdasContainer maxWidth='lg'>
      <AdasBox className='mt-5 mb-[60px]'>
        {/* Reports */}
        <AdasBox className={PANEL}>
          <AdasBox className='mb-4 flex items-center justify-between'>
            <AdasTypography className={PANEL_HEADING}>Reports</AdasTypography>
            <AdasTypography className='text-[13px] text-[#475569]'>
              <span className='font-bold text-[#0A1628]'>{reportsAttached}</span>
              <span className='ml-1'>of {reportsTotal} attached</span>
            </AdasTypography>
          </AdasBox>
          <AdasBox className='flex flex-col gap-3'>
            <FileUpload
              variant='row'
              removeFile={() => removeFileHandler('preCalibrationReport')}
              existingData={documents.preCalibrationReport}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_PRE_CALIBRATION_SCAN_REPORT}
              label={FILE_UPLOAD.LABEL.PRE_CALIBRATION_SCAN_REPORT}
              typeLabel={FILE_UPLOAD.TYPE_LABEL.PRE_CALIBRATION_SCAN_REPORT}
              isClientInvoice={true}
            />
            <FileUpload
              variant='row'
              removeFile={() => removeFileHandler('oemProcedure')}
              existingData={documents.oemProcedure}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_OEM_PROCEDURE}
              label={FILE_UPLOAD.LABEL.OEM_PROCEDURE}
              typeLabel={FILE_UPLOAD.TYPE_LABEL.OEM_PROCEDURE}
              isClientInvoice={true}
            />
            <FileUpload
              variant='row'
              removeFile={() => removeFileHandler('wheelAlignmentReport')}
              existingData={documents.wheelAlignmentReport}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_WHEEL_ALIGNMENT_REPORT}
              label={FILE_UPLOAD.LABEL.WHEEL_ALIGNMENT_REPORT}
              typeLabel={FILE_UPLOAD.TYPE_LABEL.WHEEL_ALIGNMENT_REPORT}
              isClientInvoice={true}
            />
            <FileUpload
              variant='row'
              removeFile={() => removeFileHandler('calibrationReport')}
              existingData={documents.calibrationReport}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_CALIBRATION_REPORT}
              label={FILE_UPLOAD.LABEL.CALIBRATION_REPORT}
              typeLabel={FILE_UPLOAD.TYPE_LABEL.CALIBRATION_REPORT}
              isClientInvoice={true}
            />
            <FileUpload
              variant='row'
              removeFile={() => removeFileHandler('postTestScanReport')}
              existingData={documents.postTestScanReport}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_POST_TEST_DRIVE_SCAN_REPORT}
              label={FILE_UPLOAD.LABEL.POST_TEST_DRIVE_SCAN_REPORT}
              typeLabel={FILE_UPLOAD.TYPE_LABEL.POST_TEST_DRIVE_SCAN_REPORT}
              isClientInvoice={true}
            />
          </AdasBox>
        </AdasBox>

        {/* Additional Documents */}
        <AdasBox className={PANEL}>
          <AdasBox className='mb-4 flex items-center justify-between'>
            <AdasTypography className={PANEL_HEADING}>Additional Documents</AdasTypography>
            <AdasTypography className='text-[13px] text-[#475569]'>
              <span className='font-bold text-[#0A1628]'>{additionalAttached}</span>
              <span className='ml-1'>of {additionalTotal} attached</span>
            </AdasTypography>
          </AdasBox>
          <AdasBox className='flex flex-col gap-3'>
            <FileUpload
              variant='row'
              removeFile={() => removeFileHandler('collisionRepairEstimate')}
              existingData={documents.collisionRepairEstimate}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_COLLISION_REPAIR_ESTIMATE}
              label={FILE_UPLOAD.LABEL.COLLISION_REPAIR_ESTIMATE}
              typeLabel={FILE_UPLOAD.TYPE_LABEL.PRE_CALIBRATION_REPAIR_ESTIMATE_SCAN}
              isClientInvoice={true}
            />
            <FileUpload
              variant='row'
              removeFile={() => removeFileHandler('calibrationRequiredReport')}
              existingData={documents.calibrationRequiredReport}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_CALIBRATION_REQUIRED_REPORT}
              label={FILE_UPLOAD.LABEL.CALIBRATION_REQUIRED_REPORT}
              typeLabel={FILE_UPLOAD.TYPE_LABEL.CALIBRATION_REQUIRED_REPORT}
              isClientInvoice={true}
            />
            <FileUpload
              variant='row'
              removeFile={() => removeFileHandler('partsInvoice')}
              existingData={documents.partsInvoice}
              workOrderData={workOrderData}
              title={FILE_UPLOAD.TITLE.UPLOAD_PARTS_INVOICE}
              label={FILE_UPLOAD.LABEL.PARTS_INVOICE}
              typeLabel={FILE_UPLOAD.TYPE_LABEL.PARTS_INVOICE}
              isClientInvoice={true}
            />
            {loadedAdditionalDocs.map((doc, index) =>
              doc && !doc.removed ? (
                <FileUpload
                  key={`loaded-${index}`}
                  variant='row'
                  removeFile={() => removeLoadedAdditionalFileHandler(index)}
                  existingData={doc}
                  workOrderData={workOrderData}
                  title={`custom_document_${index}`}
                  label={FILE_UPLOAD.LABEL.ADDITIONAL_DOCUMENT}
                  typeLabel={FILE_UPLOAD.TYPE_LABEL.ADDITIONAL_DOCUMENT}
                  isClientInvoice={true}
                />
              ) : null
            )}
            {additionalDocuments.map((doc, index) =>
              doc && !doc.removed ? (
                <FileUpload
                  key={`additional-${index}`}
                  variant='row'
                  removeFile={() => removeAdditionalFileHandler(index)}
                  additionalDocument={doc}
                  workOrderData={workOrderData}
                  title={`custom_document_${index}`}
                  label={FILE_UPLOAD.LABEL.ADDITIONAL_DOCUMENT}
                  typeLabel={FILE_UPLOAD.TYPE_LABEL.ADDITIONAL_DOCUMENT}
                  isClientInvoice={true}
                />
              ) : null
            )}
          </AdasBox>
        </AdasBox>

        {/* OEM Position Statement Lines */}
        {documents?.qwiOemPositionStatements?.length > 0 && (
          <AdasBox className={PANEL}>
            <AdasTypography className={`${PANEL_HEADING} mb-3`}>OEM Position Statement Lines</AdasTypography>
            {documents.qwiOemPositionStatements.map((data, index) => (
              <AdasBox key={index} className='mb-2 flex items-center'>
                {statementLoading == data.oemPositionStatements.id && <AdasCircularProgress size={15} className='mr-[10px]' />}
                <AdasLink
                  id={data.oemPositionStatements.id}
                  className='block cursor-pointer overflow-hidden text-sm text-[#1F6FEB] no-underline whitespace-nowrap hover:underline'
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={handleOemStatementClick}
                >
                  {data.oemPositionStatements.name}
                </AdasLink>
              </AdasBox>
            ))}
          </AdasBox>
        )}

        <AdasButton
          variant='outlined'
          aria-label='goBack'
          color='primary'
          className='rounded-lg px-6 font-semibold'
          onClick={() => history.goBack()}
        >
          Return To Summary
        </AdasButton>
      </AdasBox>
    </AdasContainer>
  )
}
